import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useWatch } from "react-hook-form";
import { AddFullButton } from "../../components/buttons/addFullButton";
import { Section } from "../../components/section";
import { EvaluationCriterion } from "../../schemas/types";
import { EvaluationSectionComponentProps } from "../../types/evaluationFormTypes";

export const EvaluationSectionComponent = ({ stageIndex, sectionIndex, control, register, errors, evaluationType, onDelete, onDuplicate }: EvaluationSectionComponentProps) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: `evaluations.${stageIndex}.sections.${sectionIndex}.criteria`
    });

    const [showInstructions, setShowInstructions] = useState<Record<number, boolean>>({});
    const toggleInstructions = (index: number) => {
        setShowInstructions(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const [showDescription, setShowDescription] = useState<Record<number, boolean>>({});
    const toggleDescription = (index: number, show: boolean) => {
        setShowDescription(prev => ({ ...prev, [index]: show }));
    };

    const criteria = useWatch({
        control,
        name: `evaluations.${stageIndex}.sections.${sectionIndex}.criteria`,
    });

    useEffect(() => {
        if (criteria && Array.isArray(criteria)) {
            const initialShowDescription: Record<number, boolean> = {};
            criteria.forEach((criterion: any, index: number) => {
                if (criterion?.description && criterion.description.trim() !== "") {
                    initialShowDescription[index] = true;
                }
            });
            if (Object.keys(initialShowDescription).length > 0) {
                setShowDescription(prev => ({ ...prev, ...initialShowDescription }));
            }
        }
    }, [criteria]);

    const scoreRange = useMemo(() => {
        return evaluationType.scale||[];
    }, [evaluationType]);

    const addCriteria = () => {
        append({
            name: '',
            description: '',
            weight: 0,
            scoreInstructions: {}
        });
    };

    return (
        <Section isOpen={true} label={`Seção ${sectionIndex + 1}`} hasBorder={true} hasMarginTop={true} showActions={true} onDelete={onDelete} onDuplicate={onDuplicate}>
            <Row>
                <Col>
                    <Form.Group>
                        <Form.Label required>Nome da seção</Form.Label>
                        <Form.Control
                            placeholder="Digite o nome da seção"
                            {...register(`evaluations.${stageIndex}.sections.${sectionIndex}.name`)}
                            isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.name?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            {fields.map((field, criterionIndex) => (
                <Section key={field.id} isOpen={true} label={`Critério ${criterionIndex + 1}`} hasBorder={true} hasMarginTop={false} showActions={true} onDelete={onDelete} onDuplicate={onDuplicate}>
                    <Row>
                        <Col>
                            <Form.Group controlId={`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.name`}>
                                <Form.Label required>Nome do critério</Form.Label>
                                <Form.Control
                                    placeholder="Digite o nome do critério"
                                    {...register(`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.name`)}
                                    isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.name?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={3}>
                            <Form.Group controlId={`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.weight`}>
                                <Form.Label>Peso</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.1"
                                    {...register(`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.weight`)}
                                    isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.weight}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.weight?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className={!showDescription[criterionIndex] ? "" : "d-none"}>
                        <Col>
                            <Button variant="link" onClick={() => toggleDescription(criterionIndex, true)} className="d-flex align-items-center gap-2 text-decoration-none">
                                <FontAwesomeIcon icon={['fas', 'plus']} />
                                Adicionar Descrição
                            </Button>
                        </Col>
                    </Row>
                    <Row className={showDescription[criterionIndex] ? "" : "d-none"}>
                        <Col>
                            <Form.Group controlId={`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.description`}>
                                <Form.Label>Descrição</Form.Label>
                                <div style={{ display: 'flex' }}>
                                    <Form.Control
                                        placeholder="Digite a descrição do critério"
                                        {...register(`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.description`)}
                                        isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.description}
                                    />
                                    <Button variant="link" onClick={() => toggleDescription(criterionIndex, false)} className="d-flex align-items-center gap-2 text-decoration-none">
                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                    </Button>
                                </div>
                                <Form.Control.Feedback type="invalid">
                                    {errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.description?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col>
                            <Button variant="link" onClick={() => toggleInstructions(criterionIndex)} className="d-flex align-items-center gap-2 text-decoration-none p-0">
                                <FontAwesomeIcon icon={['fas', showInstructions[criterionIndex] ? 'minus' : 'plus']} />
                                {showInstructions[criterionIndex] ? "Remover instrução para avaliação do critério" : "Adicionar instrução para avaliação do critério"}
                            </Button>
                        </Col>
                    </Row>
                    {showInstructions[criterionIndex] && (
                        <>
                            <Row className="gx-0">
                                {scoreRange.map((score: number) => (
                                    <Col md={5} lg={5} key={score}>
                                        <Form.Group controlId={`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.scoreInstructions.score${score}`}>
                                            <Form.Label>Descrição da nota {score}</Form.Label>
                                            <Form.Control
                                                placeholder={`Digite a descrição da nota ${score}`}
                                                {...register(`evaluations.${stageIndex}.sections.${sectionIndex}.criteria.${criterionIndex}.scoreInstructions.score${score}` as any)}
                                                type="text"
                                                isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.scoreInstructions?.[`score${score}` as keyof NonNullable<EvaluationCriterion['scoreInstructions']>]}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.evaluations?.[stageIndex]?.sections?.[sectionIndex]?.criteria?.[criterionIndex]?.scoreInstructions?.[`score${score}` as keyof NonNullable<EvaluationCriterion['scoreInstructions']>]?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}
                </Section>
            ))}
            <Row className="mt-3">
                <Col>
                    <AddFullButton label="Adicionar Critério" icon={faPlus} handleOnClick={addCriteria} />
                </Col>
            </Row>
        </Section>
    );
};
